import { z } from 'zod'

const requireWeeklyHours = (data: { engagementType?: string; weeklyHours?: string }, ctx: z.RefinementCtx) => {
  if (['ONGOING', 'ONE_OFF_EXTENDABLE'].includes(data.engagementType || '') && !data.weeklyHours) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['weeklyHours'],
      message: '지속형 프로젝트는 주당 시간을 선택해주세요.',
    })
  }
}

export const projectFormSchema = z
  .object({
    projectDomain: z.string().min(1, '프로젝트 분야를 선택해주세요.'),
    neededRoles: z.array(z.string()).optional(),
    projectServiceName: z.string().min(1, '프로젝트 제목을 입력해주세요.'),
    shortSummary: z.string().min(1, '한 줄 요약을 입력해주세요.').max(300, '한 줄 요약은 300자 이내로 입력해주세요.'),
    projectType: z.string().optional(),
    resultPreset: z.string().optional(),
    collaborationMode: z.enum(['TEAM', 'INDIVIDUAL']).optional(),
    functionality: z.string().min(1, '상세 브리프를 입력해주세요.'),
    period: z.string().min(1, '예상 기간을 선택해주세요.'),
    cost: z.string().min(1, '예상 예산을 선택해주세요.'),
    skills: z.array(z.string()).optional(),
    contactPhone: z
      .string()
      .min(1, '전화번호를 입력해주세요.')
      .regex(/^010-\d{3,4}-\d{4}$/, '010-0000-0000 형식으로 입력해주세요.'),
    contactEmail: z.string().min(1, '이메일을 입력해주세요.').email('이메일 형식이 올바르지 않습니다.'),
    mainCategories: z.array(z.string()).optional(),
    subCategories: z.array(z.string()).optional(),
    referenceService: z.string().optional(),
    materials: z.array(z.string()).optional(),
    designLevel: z.string().min(1, '경험 수준을 선택해주세요.'),
    engagementType: z.string().min(1, '프로젝트 유형(지속성)을 선택해주세요.'),
    weeklyHours: z.string().optional(),
    otherRequirements: z.string().optional(),
    startDate: z.string().optional(),
    workType: z.string().optional(),
  })
  .superRefine(requireWeeklyHours)

export const contactInfoSchema = z.object({
  contactPhone: z
    .string()
    .min(1, '전화번호를 입력해주세요.')
    .regex(/^010-\d{3,4}-\d{4}$/, '010-0000-0000 형식으로 입력해주세요.'),
  contactEmail: z.string().min(1, '이메일을 입력해주세요.').email('이메일 형식이 올바르지 않습니다.'),
})

export const projectOverviewSchema = z.object({
  resultPreset: z.string().min(1, '템플릿을 선택해주세요.'),
  projectServiceName: z.string().min(1, '프로젝트 제목을 입력해주세요.'),
  shortSummary: z.string().min(1, '한 줄 요약을 입력해주세요.').max(300, '한 줄 요약은 300자 이내로 입력해주세요.'),
  projectType: z.string().optional(),
  // 자동 세팅 (UI에서 미노출): projectDomain, neededRoles, collaborationMode
  projectDomain: z.string().optional(),
  neededRoles: z.array(z.string()).optional(),
  collaborationMode: z.enum(['TEAM', 'INDIVIDUAL']).optional(),
})

export const workScopeSchema = z
  .object({
    functionality: z.string().min(1, '상세 브리프를 입력해주세요.'),
    designLevel: z.string().min(1, '요구되는 숙련도를 선택해주세요.'),
    engagementType: z.string().min(1, '진행 형태를 선택해주세요.'),
    weeklyHours: z.string().optional(),
    mainCategories: z.array(z.string()).optional(),
    subCategories: z.array(z.string()).optional(),
    skills: z.array(z.string()).optional(),
    workType: z.string().optional(),
    materials: z.array(z.string()).optional(),
    referenceService: z.string().optional(),
    otherRequirements: z.string().optional(),
  })
  .superRefine(requireWeeklyHours)

export const budgetScheduleSchema = z.object({
  period: z.string().min(1, '예상 기간을 선택해주세요.'),
  cost: z.string().min(1, '예상 예산을 선택해주세요.'),
  startDate: z.string().optional(),
})

export type ProjectFormData = z.infer<typeof projectFormSchema>
export type ContactInfoData = z.infer<typeof contactInfoSchema>
export type ProjectOverviewData = z.infer<typeof projectOverviewSchema>
export type WorkScopeData = z.infer<typeof workScopeSchema>
export type BudgetScheduleData = z.infer<typeof budgetScheduleSchema>
